import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { RequireCapability } from "@/components/RequireCapability";
import { useAuth } from "@/lib/auth/auth-context";

import { ExpertArea } from "./components/ExpertArea";
import { ProfileView } from "./components/ProfileView";
import { AdminApp } from "./components/admin/AdminApp";
import { DashboardView } from "./components/DashboardView";
import { EmailConfirmationGate } from "./components/registration/EmailConfirmationGate";
import { ObjectivesFlow } from "./components/registration/ObjectivesFlow";
import { RegistrationFlow } from "./components/registration/RegistrationFlow";
// Registers the slice's expert.* translations (side-effect import).
import "./i18n";

/**
 * Expert directory area (route: /experts). The directory + public profiles are
 * reached contextually from an action; gated to any authenticated user (each
 * surface handles its own access).
 */
export function ExpertPage() {
  return (
    <RequireCapability required={["authenticated"]}>
      <ExpertArea />
    </RequireCapability>
  );
}

/**
 * The expert's own dashboard (route: /experts/dashboard) — their public profile,
 * stats, mandates, and (for admins) the invitation queue. Expert-gated.
 */
export function ExpertDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <RequireCapability required={["expert"]}>
      {user && !user.email_verified ? (
        <div className="mx-auto max-w-3xl">
          <EmailConfirmationGate />
        </div>
      ) : (
        <DashboardView
          onGoRegister={() => void navigate({ to: "/experts/register" })}
        />
      )}
    </RequireCapability>
  );
}

/**
 * The self-registration wizard (route: /experts/register) — server-persisted,
 * resumable. Expert-gated (the account is marked Expert at signup).
 */
export function ExpertRegisterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <RequireCapability required={["expert"]}>
      <div className="mx-auto max-w-3xl">
        {user && !user.email_verified ? (
          <EmailConfirmationGate />
        ) : (
          <RegistrationFlow
            onDone={() => void navigate({ to: "/experts/dashboard" })}
          />
        )}
      </div>
    </RequireCapability>
  );
}

/**
 * Part 2 of registration (route: /experts/objectives) — objectives + action
 * paths, unlocked after CQDD approval. Reached from the dashboard call-to-action
 * and the approval email link. Expert-gated; non-approved experts are redirected
 * back to the dashboard by the flow.
 */
export function ExpertObjectivesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <RequireCapability required={["expert"]}>
      <div className="mx-auto max-w-3xl">
        {user && !user.email_verified ? (
          <EmailConfirmationGate />
        ) : (
          <ObjectivesFlow
            onDone={() => void navigate({ to: "/experts/dashboard" })}
          />
        )}
      </div>
    </RequireCapability>
  );
}

/**
 * A validated expert's public profile (route: /experts/profile/$id). Opened in a
 * new tab from a directory card (with `?action=` → the ♥ favorite toggle shows)
 * and from the expert's own dashboard ("Voir mon profil public", no action).
 */
export function ExpertProfilePage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const search = useSearch({ strict: false }) as { action?: unknown };
  const action = typeof search.action === "string" ? search.action : undefined;
  return (
    <RequireCapability required={["authenticated"]}>
      <div className="mx-auto max-w-4xl">
        <ProfileView
          expertId={params.id ?? ""}
          action={action}
          onBack={() => {
            if (action) void navigate({ to: "/experts", search: { action } });
            else void navigate({ to: "/experts/dashboard" });
          }}
        />
      </div>
    </RequireCapability>
  );
}

/**
 * The CQDD expert back-office (route: /experts/admin) — list, review decisions,
 * invitations, imports and export. Admin-gated (admin/Django superuser only).
 */
export function ExpertAdminPage() {
  return (
    <RequireCapability required={["expertAdmin"]}>
      <AdminApp />
    </RequireCapability>
  );
}
